/**
 * Metro 2 Dispute Item Selection Screen
 * 
 * Technical, field-level, contradiction-driven interface.
 * Evidence-backed and structured for investigator-grade review.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  ArrowLeft,
  Database,
  AlertTriangle,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  Info,
  Zap,
  FileText,
  Eye,
  Trash2,
  Plus,
  Code,
  Activity,
  TrendingUp,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../components/ui/sheet';

// Bureau colors
const BUREAU_COLORS = {
  TransUnion: 'bg-emerald-100 text-emerald-700',
  Experian: 'bg-amber-100 text-amber-700',
  Equifax: 'bg-red-100 text-red-700',
};

// Priority badges
const PRIORITY_BADGES = {
  high: { label: 'High Priority', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  medium: { label: 'Medium Priority', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  low: { label: 'Low Priority', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
};

// Confidence level colors
const getConfidenceColor = (confidence) => {
  if (confidence >= 0.8) return 'text-emerald-400';
  if (confidence >= 0.6) return 'text-amber-400';
  return 'text-red-400';
};

// Metro 2 item detail drawer component
const Metro2DetailDrawer = ({ item, isOpen, onClose, itemDetails, onUpdateDetails }) => {
  if (!item) return null;
  
  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl bg-stone-900 border-slate-700 overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-white">
            {item.creditor_name || item.name}
          </SheetTitle>
          <SheetDescription className="text-slate-400">
            Metro 2 Field Analysis
          </SheetDescription>
        </SheetHeader>
        
        <div className="mt-6 space-y-6">
          {/* Why Selected */}
          <section>
            <h3 className="text-sm font-medium text-slate-400 mb-2">Why AI Selected This</h3>
            <div className="p-4 bg-stone-800/50 rounded-lg border border-slate-700">
              <p className="text-white">{item.ai_reason || 'Field-level inconsistencies detected.'}</p>
            </div>
          </section>
          
          {/* Reporting Issue */}
          <section>
            <h3 className="text-sm font-medium text-slate-400 mb-2">Reporting Issue</h3>
            <div className="p-4 bg-stone-800/50 rounded-lg border border-slate-700">
              <p className="text-white">{item.issue_description || 'Data inconsistency detected in Metro 2 format fields.'}</p>
            </div>
          </section>
          
          {/* Fields Affected */}
          <section>
            <h3 className="text-sm font-medium text-slate-400 mb-2">Fields Affected</h3>
            <div className="space-y-2">
              {(item.fields_affected || ['Current Balance (BS-7)', 'Highest Balance (BS-17B)']).map((field, i) => (
                <div key={i} className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  <Code size={16} className="text-emerald-400" />
                  <span className="text-emerald-300 font-mono text-sm">{field}</span>
                </div>
              ))}
            </div>
          </section>
          
          {/* Exact Extracted Text */}
          <section>
            <h3 className="text-sm font-medium text-slate-400 mb-2">Exact Extracted Text</h3>
            <div className="p-4 bg-stone-950 rounded-lg border border-slate-700 font-mono text-sm text-slate-300">
              <pre className="whitespace-pre-wrap">
{item.extracted_text || `Current Balance: $4,523.00
Highest Balance: $3,200.00
Date Opened: 03/2019
Last Payment: $0.00`}
              </pre>
            </div>
          </section>
          
          {/* Highlighted Source Snippet */}
          <section>
            <h3 className="text-sm font-medium text-slate-400 mb-2">Highlighted Source</h3>
            <div className="p-4 bg-stone-800/50 rounded-lg border border-slate-700">
              <div className="text-sm text-slate-300">
                {item.source_snippet ? (
                  <p dangerouslySetInnerHTML={{ __html: item.source_snippet }} />
                ) : (
                  <p>
                    Account shows <mark className="bg-amber-500/30 text-amber-200 px-1 rounded">$4,523.00</mark> current balance 
                    but highest balance ever reported is only <mark className="bg-red-500/30 text-red-200 px-1 rounded">$3,200.00</mark>.
                    This is a mathematical impossibility.
                  </p>
                )}
              </div>
            </div>
          </section>
          
          {/* Rule Explanation */}
          <section>
            <h3 className="text-sm font-medium text-slate-400 mb-2">Rule Explanation</h3>
            <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
              <p className="text-emerald-300 text-sm">
                {item.rule_explanation || 'Metro 2 Format requires Current Balance (Field BS-7) to never exceed Highest Balance (Field BS-17B). This violation indicates a reporting error.'}
              </p>
            </div>
          </section>
          
          {/* Suggested Dispute Framing */}
          <section>
            <h3 className="text-sm font-medium text-slate-400 mb-2">Suggested Dispute Framing</h3>
            <div className="p-4 bg-stone-800/50 rounded-lg border border-slate-700">
              <p className="text-white text-sm italic">
                "{item.dispute_framing || 'The current balance reported exceeds the highest balance ever reported, which is mathematically impossible and indicates a data integrity violation.'}"
              </p>
            </div>
          </section>
          
          {/* User Input Section */}
          <section>
            <h3 className="text-sm font-medium text-slate-400 mb-2">Your Input</h3>
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-slate-300 mb-2 block">Dispute Reason</Label>
                <Textarea
                  value={itemDetails?.reason || ''}
                  onChange={(e) => onUpdateDetails(item.id, { reason: e.target.value })}
                  placeholder="Add your reason for disputing this item..."
                  className="bg-stone-800 border-slate-600 text-white"
                  rows={3}
                />
              </div>
              <div>
                <Label className="text-sm text-slate-300 mb-2 block">Instructions</Label>
                <Textarea
                  value={itemDetails?.instructions || ''}
                  onChange={(e) => onUpdateDetails(item.id, { instructions: e.target.value })}
                  placeholder="Add any specific instructions..."
                  className="bg-stone-800 border-slate-600 text-white"
                  rows={2}
                />
              </div>
            </div>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Metro 2 item card component
const Metro2ItemCard = ({
  item,
  isIncluded,
  onToggleInclude,
  onReviewDetails,
  itemDetails,
  onUpdateDetails,
}) => {
  const [expanded, setExpanded] = useState(false);
  const details = itemDetails || {};
  const priority = item.priority || 'medium';
  const priorityBadge = PRIORITY_BADGES[priority];
  const confidence = item.confidence_score || 0.75;
  
  const hasReason = !!details.reason;
  const hasInstructions = !!details.instructions;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`border rounded-xl overflow-hidden transition-all ${
        isIncluded 
          ? 'border-emerald-500/50 bg-emerald-500/5' 
          : 'border-slate-700 bg-stone-800/50'
      }`}
      data-testid={`metro2-item-${item.id}`}
    >
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {/* Title and badges */}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <h4 className="font-semibold text-white">
                {item.creditor_name || item.name || 'Unknown Account'}
              </h4>
              {item.account_mask && (
                <span className="text-slate-400 text-sm">•••• {item.account_mask}</span>
              )}
            </div>
            
            {/* Type and bureaus */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Badge variant="outline" className="text-xs">
                {item.account_type || item.type || 'Account'}
              </Badge>
              {(item.bureaus || [item.bureau]).map((bureau) => (
                <Badge key={bureau} className={BUREAU_COLORS[bureau] || 'bg-slate-700 text-slate-300'}>
                  {bureau}
                </Badge>
              ))}
            </div>
            
            {/* Priority and status badges */}
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={`border ${priorityBadge.color}`}>
                {priorityBadge.label}
              </Badge>
              {item.has_strong_contradiction && (
                <Badge className="bg-red-500/20 text-red-400 border border-red-500/30">
                  Strong Contradiction
                </Badge>
              )}
              <Badge className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                <Code size={12} className="mr-1" />
                Metro 2 Fields Affected
              </Badge>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex flex-col items-end gap-2">
            <Button
              variant={isIncluded ? "default" : "outline"}
              size="sm"
              onClick={() => onToggleInclude(item.id)}
              className={isIncluded ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
            >
              {isIncluded ? (
                <>
                  <Check size={14} className="mr-1" />
                  Included
                </>
              ) : (
                <>
                  <Plus size={14} className="mr-1" />
                  Include
                </>
              )}
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onReviewDetails(item)}
              className="text-slate-400 hover:text-white"
            >
              <Eye size={14} className="mr-1" />
              Review Details
            </Button>
          </div>
        </div>
        
        {/* AI Reason */}
        <div className="mt-3 p-3 bg-slate-700/30 rounded-lg">
          <p className="text-xs text-slate-400 mb-1 flex items-center gap-1">
            <Zap size={12} className="text-emerald-400" />
            Why AI selected this:
          </p>
          <p className="text-sm text-slate-300">
            {item.ai_reason || 'Field-level inconsistencies detected that may support a technical dispute.'}
          </p>
        </div>
        
        {/* Detail Summary */}
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <div className="p-2 bg-stone-800/50 rounded">
            <p className="text-slate-500">Fields affected</p>
            <p className="text-white font-medium">{(item.fields_affected || []).length || 2}</p>
          </div>
          <div className="p-2 bg-stone-800/50 rounded">
            <p className="text-slate-500">Issue type</p>
            <p className="text-white font-medium truncate">{item.issue_type || 'Inconsistency'}</p>
          </div>
          <div className="p-2 bg-stone-800/50 rounded">
            <p className="text-slate-500">Confidence</p>
            <p className={`font-medium ${getConfidenceColor(confidence)}`}>
              {Math.round(confidence * 100)}%
            </p>
          </div>
          <div className="p-2 bg-stone-800/50 rounded">
            <p className="text-slate-500">Dispute strength</p>
            <p className={`font-medium ${confidence >= 0.7 ? 'text-emerald-400' : 'text-amber-400'}`}>
              {confidence >= 0.8 ? 'High' : confidence >= 0.6 ? 'Medium' : 'Low'}
            </p>
          </div>
        </div>
        
        {/* Status chips */}
        <div className="mt-3 flex items-center gap-2">
          <span className={`text-xs px-2 py-1 rounded ${
            hasReason 
              ? 'bg-emerald-500/20 text-emerald-400' 
              : 'bg-amber-500/20 text-amber-400'
          }`}>
            {hasReason ? 'Reason Added' : 'Needs Reason'}
          </span>
          <span className={`text-xs px-2 py-1 rounded ${
            hasInstructions 
              ? 'bg-emerald-500/20 text-emerald-400' 
              : 'bg-slate-600 text-slate-400'
          }`}>
            {hasInstructions ? 'Instruction Added' : 'Needs Instruction'}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

// Main component
const Metro2DisputeSelectionScreen = ({
  findings,
  onContinue,
  onBack,
}) => {
  // State
  const [includedItems, setIncludedItems] = useState(new Set());
  const [itemDetails, setItemDetails] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  
  // Categorize findings
  const categorizedFindings = {
    accounts: findings?.filter(f => 
      f.type === 'account' || f.type === 'tradeline' || !f.type?.includes('inquiry')
    ) || [],
    inquiries: findings?.filter(f => 
      f.type === 'inquiry'
    ) || [],
    personalInfo: findings?.filter(f => 
      f.type === 'personal_info'
    ) || [],
  };
  
  // Initialize included items from recommendations
  useEffect(() => {
    const recommended = new Set(
      findings?.filter(f => f.recommended || f.priority === 'high')?.map(f => f.id) || []
    );
    setIncludedItems(recommended);
  }, [findings]);
  
  const toggleInclude = (itemId) => {
    setIncludedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };
  
  const updateItemDetails = (itemId, updates) => {
    setItemDetails(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], ...updates },
    }));
  };
  
  const handleReviewDetails = (item) => {
    setSelectedItem(item);
    setShowDetailDrawer(true);
  };
  
  const handleContinue = () => {
    onContinue({
      includedItems: Array.from(includedItems),
      itemDetails,
    });
  };
  
  // Summary counts
  const highPriorityCount = findings?.filter(f => f.priority === 'high')?.length || 0;
  const needsInputCount = findings?.filter(f => !itemDetails[f.id]?.reason)?.length || 0;
  const strongContradictions = findings?.filter(f => f.has_strong_contradiction)?.length || 0;
  
  return (
    <div className="min-h-screen bg-stone-900 py-8 px-4" data-testid="metro2-dispute-selection">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" onClick={onBack} className="mb-4">
            <ArrowLeft size={18} className="mr-1" />
            Back
          </Button>
          
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <Database size={24} className="text-emerald-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">AI Recommended Metro 2 Dispute Items</h1>
            </div>
          </div>
          <p className="text-slate-400 mt-2">
            We found reporting inconsistencies and field-level issues that may support a technical dispute. 
            Review the items below, remove anything you do not want included, and complete any missing inputs before continuing.
          </p>
        </div>
        
        {/* Summary Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-stone-800/50 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle size={16} className="text-red-400" />
              <p className="text-xs text-slate-400">High-priority findings</p>
            </div>
            <p className="text-2xl font-bold text-red-400">{highPriorityCount}</p>
          </div>
          <div className="bg-stone-800/50 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <FileText size={16} className="text-amber-400" />
              <p className="text-xs text-slate-400">Need your input</p>
            </div>
            <p className="text-2xl font-bold text-amber-400">{needsInputCount}</p>
          </div>
          <div className="bg-stone-800/50 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <Zap size={16} className="text-emerald-400" />
              <p className="text-xs text-slate-400">Strong contradictions</p>
            </div>
            <p className="text-2xl font-bold text-emerald-400">{strongContradictions}</p>
          </div>
          <div className="bg-stone-800/50 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <Activity size={16} className="text-emerald-400" />
              <p className="text-xs text-slate-400">Package strategy</p>
            </div>
            <p className="text-lg font-bold text-emerald-400">Bureau Package</p>
          </div>
        </div>
        
        {/* Item Sections */}
        <div className="space-y-8">
          {/* Recommended Accounts */}
          {categorizedFindings.accounts.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <TrendingUp size={20} className="text-emerald-400" />
                Recommended Accounts
                <Badge variant="outline" className="ml-2">{categorizedFindings.accounts.length}</Badge>
              </h2>
              <div className="space-y-4">
                {categorizedFindings.accounts.map((item) => (
                  <Metro2ItemCard
                    key={item.id}
                    item={item}
                    isIncluded={includedItems.has(item.id)}
                    onToggleInclude={toggleInclude}
                    onReviewDetails={handleReviewDetails}
                    itemDetails={itemDetails[item.id]}
                    onUpdateDetails={updateItemDetails}
                  />
                ))}
              </div>
            </section>
          )}
          
          {/* Recommended Inquiries */}
          {categorizedFindings.inquiries.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Search size={20} className="text-amber-400" />
                Recommended Inquiries
                <Badge variant="outline" className="ml-2">{categorizedFindings.inquiries.length}</Badge>
              </h2>
              <div className="space-y-4">
                {categorizedFindings.inquiries.map((item) => (
                  <Metro2ItemCard
                    key={item.id}
                    item={item}
                    isIncluded={includedItems.has(item.id)}
                    onToggleInclude={toggleInclude}
                    onReviewDetails={handleReviewDetails}
                    itemDetails={itemDetails[item.id]}
                    onUpdateDetails={updateItemDetails}
                  />
                ))}
              </div>
            </section>
          )}
          
          {/* Personal Information Conflicts */}
          {categorizedFindings.personalInfo.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <User size={20} className="text-amber-400" />
                Recommended Personal Information Conflicts
                <Badge variant="outline" className="ml-2">{categorizedFindings.personalInfo.length}</Badge>
              </h2>
              <div className="space-y-4">
                {categorizedFindings.personalInfo.map((item) => (
                  <Metro2ItemCard
                    key={item.id}
                    item={item}
                    isIncluded={includedItems.has(item.id)}
                    onToggleInclude={toggleInclude}
                    onReviewDetails={handleReviewDetails}
                    itemDetails={itemDetails[item.id]}
                    onUpdateDetails={updateItemDetails}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
        
        {/* Detail Drawer */}
        <Metro2DetailDrawer
          item={selectedItem}
          isOpen={showDetailDrawer}
          onClose={() => setShowDetailDrawer(false)}
          itemDetails={itemDetails[selectedItem?.id]}
          onUpdateDetails={updateItemDetails}
        />
        
        {/* Navigation */}
        <div className="mt-8 flex justify-between items-center sticky bottom-4 bg-stone-900/95 backdrop-blur p-4 rounded-xl border border-slate-700">
          <div className="text-sm text-slate-400">
            <span className="text-white font-medium">{includedItems.size}</span> items selected for dispute
          </div>
          
          <Button 
            onClick={handleContinue}
            disabled={includedItems.size === 0}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="continue-metro2-btn"
          >
            Continue to Review
            <ArrowRight size={18} className="ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Missing import
const Search = ({ size, className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="11" cy="11" r="8"></circle>
    <path d="m21 21-4.3-4.3"></path>
  </svg>
);

const User = ({ size, className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

export default Metro2DisputeSelectionScreen;
